import { awaitDataServiceFetcher } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/DataServiceFetchers';
import { normalizeEmptyResponseToSuccessResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/infrastructure';
import { SuccessCallback } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { CardHeaders } from '../../../../../userProfile/src/domain/UserInfo/entities';
import { DownloadTypes } from '../../../../../content-composer/src/components/Generic/FileDownload/FileDownload';
import { ICardRepository } from '../entities/ICardRepository';
import { CardTransactionDetails, CardTransactionsDownloadResponseBody } from '../entities/CardTransaction';
import {
  CardInfoListResponseBody,
  CardInfoListRequestBody,
  CardDetailsRequestBody,
  CardDetailsResponseBody,
  CardTransactionsResponseBody,
  CardTransactionsRequestBody,
  PartyIdentification,
  CardInfoListFilter,
  CorporateCardInfoListRequestBody,
} from '../../../types';
import { CardAction } from '../../../contexts/CardActions';
import { FreezeCardResponseBody } from '../entities/Card/FreezeCardResponseDTO';
import { cardResponseDTOToCardsAdapter } from './CardResponseDTOToCardsAdapter';
import { cardTransactionsResponseDTOToCardTransactionsAdapter } from './CardTransactionsResponseDTOToCardTransactionsAdapter';
import { cardTransactionDetailsResponseDTOtoCardTransactionDetailsAdapter } from './CardTransactionDetailsResponseDTOtoCardTransactionDetailsAdapter';
import { cardDetailsResponseDTOToCardDetailsAdapter } from './CardDetailsResponseDTOToCardDetailsAdapter';
import { CardTransactionsDownloadResponseDTOToCardTransactionsDownloadAdapter } from './CardTransactionsDownloadResponseDTOToCardTransactionsDownloadAdapter';
import { cardStatusesResponseDTOtoCardStatusesAdapter } from './CardStatusesResponseDTOtoCardStatusesAdapter';
import { cardTermsPDFResponseDTOToCardTermsPDFAdapter } from './CardTermsPDFResponseDTOToCardTermsPDFAdapter';

export class CardRepositoryImpl implements ICardRepository {
  constructor() {
    this.GetIndividualCards = this.GetIndividualCards.bind(this);
    this.GetCorporateCards = this.GetCorporateCards.bind(this);
    this.GetCardStatuses = this.GetCardStatuses.bind(this);
    this.GetCardTransactions = this.GetCardTransactions.bind(this);
    this.GetCardTransactionDetails = this.GetCardTransactionDetails.bind(this);
    this.FreezeCardAction = this.FreezeCardAction.bind(this);
    this.GetCardDetails = this.GetCardDetails.bind(this);
    this.GetCardTransactionsDownloadFile = this.GetCardTransactionsDownloadFile.bind(this);
    this.GetCardTransactionsDetailsDownloadFile = this.GetCardTransactionsDetailsDownloadFile.bind(this);
  }

  GetIndividualCards({
    headers,
    customerId,
    filter,
    pagination,
  }: CardInfoListRequestBody): Promise<CardInfoListResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: '/secure-web/dx4b/customer-products/card-info/list',
        dataAdapter: cardResponseDTOToCardsAdapter,
        headers,
        data: {
          headers: [],
          body: {
            partyIdentifications: [
              {
                type: 'CoreBankingIdentifier',
                identifier: {
                  value: customerId,
                  issuingAuthority: 'Profits',
                },
              },
            ],
            filter,
          },
          pagination,
        },
      },
    });
  }

  GetCorporateCards({
    headers,
    customerId,
    filter,
    pagination,
  }: CorporateCardInfoListRequestBody): Promise<CardInfoListResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: '/secure-web/dx4b/customer-products/card-info/corporate/list',
        dataAdapter: cardResponseDTOToCardsAdapter,
        headers,
        data: {
          headers: [],
          body: {
            partyIdentifications: [
              {
                type: 'CoreBankingIdentifier',
                identifier: {
                  value: customerId,
                  issuingAuthority: 'Profits',
                },
              },
            ],
            filter,
          },
          pagination,
        },
      },
    });
  }

  GetCardStatuses(headers: CardHeaders): Promise<CardInfoListFilter> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: '/secure-web/dx4b/customer-products/card-info/statuses',
        dataAdapter: cardStatusesResponseDTOtoCardStatusesAdapter,
        headers,
      },
    });
  }

  GetCardDetails({
    headers,
    customerId,
    paymentCardIdentifications,
    nextCallback,
  }: CardDetailsRequestBody): Promise<CardDetailsResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: '/secure-web/dx4b/payment-card/prepaid/details',
        dataAdapter: cardDetailsResponseDTOToCardDetailsAdapter,
        headers,
        nextCallback,
        data: {
          headers: [],
          body: {
            partyIdentifications: [
              {
                type: 'CoreBankingIdentifier',
                identifier: {
                  value: customerId,
                  issuingAuthority: 'Profits',
                },
              },
            ],
            paymentCardIdentifications,
          },
        },
      },
    });
  }

  GetCardTransactions({
    headers,
    customerId,
    paymentCardIdentifications,
    filters,
  }: CardTransactionsRequestBody): Promise<CardTransactionsResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/secure-web/dx4b/payment-card/prepaid/transactions`,
        headers,
        data: {
          headers: [],
          body: {
            partyIdentifications: [
              {
                type: 'CoreBankingIdentifier',
                identifier: {
                  value: customerId,
                  issuingAuthority: 'Profits',
                },
              },
            ],
            paymentCardIdentifications,
            filter: Object.assign(
              {
                datePeriod: {
                  startDate: filters.dateFrom,
                  endDate: filters.dateTo,
                },
                amountFilter: Object.assign(
                  {},
                  filters.amountFrom && { fromValue: filters.amountFrom },
                  filters.amountTo && { toValue: filters.amountTo },
                ),
              },
              filters.merchant && { merchantName: filters.merchant },
              filters.status && { status: filters.status },
            ),
          },
          pagination: {
            pageNumber: filters.pageNumber,
            pageSize: filters.pageSize,
          },
        },
        dataAdapter: cardTransactionsResponseDTOToCardTransactionsAdapter,
      },
    });
  }

  GetCardTransactionDetails(customerId: string, transactionNo: number): Promise<CardTransactionDetails> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/cards/${customerId}/transaction/${transactionNo}`,
        dataAdapter: cardTransactionDetailsResponseDTOtoCardTransactionDetailsAdapter,
      },
    });
  }

  GetTopUpAuthorization(): Promise<{
    authenticationRequestId: string;
    authenticationMethod: string;
    authorizationId: number;
  }> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: '/secure-web/api/private/cards/topup',
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
      },
    });
  }

  PostTopUpCard(data: { amount: string; from: string }): Promise<{}> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        data,
        url: '/secure-web/api/private/cards/topup/post',
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
      },
    });
  }

  FreezeCardAction(
    headers: CardHeaders,
    customerId: string,
    action: CardAction,
    paymentCardIdentifications: PartyIdentification[],
    successCallback: SuccessCallback,
  ): Promise<FreezeCardResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/secure-web/dx4b/payment-card/prepaid/freeze-unfreeze`,
        headers,
        data: {
          headers: [],
          body: {
            partyIdentifications: [
              {
                type: 'CoreBankingIdentifier',
                identifier: {
                  value: customerId,
                  issuingAuthority: 'Profits',
                },
              },
            ],
            paymentCardIdentifications,
            paymentCardStatusAction: action,
          },
        },
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
        nextCallback: successCallback,
      },
    });
  }

  GetCardTransactionsDownloadFile({
    headers,
    customerId,
    paymentCardIdentifications,
    filters,
    type,
  }: CardTransactionsRequestBody & { type: DownloadTypes }): Promise<CardTransactionsDownloadResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/secure-web/dx4b/payment-card/prepaid/transactions/export`,
        headers,
        data: {
          headers: [],
          body: {
            partyIdentifications: [
              {
                type: 'CoreBankingIdentifier',
                identifier: {
                  value: customerId,
                  issuingAuthority: 'Profits',
                },
              },
            ],
            paymentCardIdentifications,
            filter: Object.assign(
              {
                datePeriod: {
                  startDate: filters.dateFrom,
                  endDate: filters.dateTo,
                },
                amountFilter: Object.assign(
                  {},
                  filters.amountFrom && { fromValue: filters.amountFrom },
                  filters.amountTo && { toValue: filters.amountTo },
                ),
              },
              filters.merchant && { merchantName: filters.merchant },
              filters.status && { status: filters.status },
            ),
            type,
          },
        },
        dataAdapter: CardTransactionsDownloadResponseDTOToCardTransactionsDownloadAdapter,
      },
    });
  }

  GetCardTransactionsDetailsDownloadFile({
    headers,
    customerId,
    paymentCardIdentifications,
    filters,
    type,
  }: CardTransactionsRequestBody & { type: DownloadTypes }): Promise<CardTransactionsDownloadResponseBody> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: `/secure-web/dx4b/payment-card/prepaid/transaction/details/export`,
        headers,
        data: {
          headers: [],
          body: {
            partyIdentifications: [
              {
                type: 'CoreBankingIdentifier',
                identifier: {
                  value: customerId,
                  issuingAuthority: 'Profits',
                },
              },
            ],
            paymentCardIdentifications,
            filter: Object.assign(
              {
                datePeriod: {
                  startDate: filters.dateFrom,
                  endDate: filters.dateTo,
                },
                transactionIdentification: filters.transactionIdentification,
                amountFilter: Object.assign(
                  {},
                  filters.amountFrom && { fromValue: filters.amountFrom },
                  filters.amountTo && { toValue: filters.amountTo },
                ),
              },
              filters.merchant && { merchantName: filters.merchant },
              filters.status && { status: filters.status },
            ),
            type,
          },
        },
        dataAdapter: CardTransactionsDownloadResponseDTOToCardTransactionsDownloadAdapter,
      },
    });
  }
  //TODO: DEPRECATED
  GetTermsAndConditionsPDF({ fileName }: { fileName: string }): Promise<any> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/dx4b/payment-card/terms/${fileName}`,
        dataAdapter: cardTermsPDFResponseDTOToCardTermsPDFAdapter,
      },
    });
  }
}
